import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import complianceService from "services/compliance";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import StatusBadge from "./StatusBadge";
import { formatDateDisplay } from "./utils";

const DEFAULT_PAGE_SIZE = 20;
const SORT_OPTIONS = [
  { value: "rank:desc", label: "Priority (High → Low)" },
  { value: "dueInDays:asc", label: "Due Soon" },
  { value: "score:desc", label: "Risk Score" },
];

const isDev = process.env.NODE_ENV === "development";
const logDebug = (...args) => {
  if (isDev && typeof console !== "undefined" && console.debug) {
    // eslint-disable-next-line no-console
    console.debug("[ComplianceAnalysis]", ...args);
  }
};

const ComplianceAnalysis = ({ scope, scopeId }) => {
  const [response, setResponse] = useState({
    data: [],
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState(SORT_OPTIONS[0].value);
  const [stateFilter, setStateFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasScope = Boolean(scope && scopeId);

  useEffect(() => {
    setPage(1);
  }, [scope, scopeId]);

  useEffect(() => {
    if (!hasScope) {
      setResponse({ data: [], page: 1, limit, total: 0 });
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchAnalysis = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await complianceService.fetchComplianceAnalysis({
          scope,
          scopeId,
          page,
          limit,
          sort,
          state: stateFilter,
        });
        if (!isMounted) return;

        const normalized = {
          data: data?.data || [],
          page: data?.page || page,
          limit: data?.limit || limit,
          total: data?.total || 0,
        };

        setResponse(normalized);
        logDebug("Analysis payload received", normalized);
      } catch (requestError) {
        if (!isMounted) return;
        setError(
          requestError?.message ||
            "We couldn't fetch the compliance tasks right now. Please try again."
        );
        logDebug("Analysis request failed", requestError);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAnalysis();

    return () => {
      isMounted = false;
    };
  }, [hasScope, scope, scopeId, page, limit, sort, stateFilter]);

  const rows = useMemo(() => response.data || [], [response.data]);
  const availableStates = useMemo(() => {
    const lookup = new Set();
    rows.forEach((item) => {
      if (item?.state) {
        lookup.add(item.state);
      }
    });
    return Array.from(lookup);
  }, [rows]);

  const totalPages = useMemo(() => {
    if (!response.limit) return 1;
    return Math.max(1, Math.ceil((response.total || 0) / response.limit));
  }, [response.limit, response.total]);

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleSortChange = (event) => {
    setSort(event.target.value);
    setPage(1);
  };

  const handleStateChange = (event) => {
    setStateFilter(event.target.value);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:flex-row md:items-end md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Compliance Tasks
          </h3>
          <p className="text-sm text-gray-600">
            Prioritized queue generated from the Markov analysis for this scope.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Sort
            </span>
            <select
              value={sort}
              onChange={handleSortChange}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-sflPrimary focus:outline-none"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              State
            </span>
            <select
              value={stateFilter}
              onChange={handleStateChange}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-sflPrimary focus:outline-none"
            >
              <option value="all">All</option>
              {availableStates.map((value) => (
                <option key={value} value={value}>
                  {formatStateLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Page Size
            </span>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-sflPrimary focus:outline-none"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {!hasScope && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-600">
          Select a company or corporate to view the analysis.
        </div>
      )}

      {hasScope && (
        <section className="space-y-4">
          {loading && (
            <div className="flex min-h-[160px] items-center justify-center rounded-xl border border-gray-200 bg-white">
              <SpinnerIcon className="h-6 w-6 animate-spin text-sflPrimary" />
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
              No prioritized tasks available right now.
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <div className="space-y-4">
              {rows.map((entity) => (
                <EntityAnalysisCard
                  key={entity.entityId || entity.entityName}
                  entity={entity}
                />
              ))}
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <PaginationControls
              page={response.page}
              totalPages={totalPages}
              onChange={setPage}
            />
          )}
        </section>
      )}
    </div>
  );
};

ComplianceAnalysis.propTypes = {
  scope: PropTypes.oneOf(["company", "corporate", "orgAdmin"]).isRequired,
  scopeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ComplianceAnalysis.defaultProps = {
  scopeId: null,
};

const EntityAnalysisCard = ({ entity }) => {
  const stateBadge = resolveStateBadge(entity?.state);
  const topTasks = Array.isArray(entity?.pagerankTopTasks)
    ? entity.pagerankTopTasks
    : [];

  return (
    <article className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Entity
          </p>
          <h4 className="text-xl font-semibold text-gray-900">
            {entity?.entityName || entity?.code || "Unnamed Entity"}
          </h4>
          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
            {entity?.score != null && (
              <span className="font-medium text-gray-700">
                Score: {Number(entity.score).toFixed(3)}
              </span>
            )}
            {entity?.p_noncompliant_k != null && (
              <span>
                P(non-compliant@k): {Number(entity.p_noncompliant_k).toFixed(3)}
              </span>
            )}
            {entity?.updatedAt && (
              <span>Updated {formatDateDisplay(entity.updatedAt)}</span>
            )}
          </div>
        </div>
        <div className="flex items-start gap-3 md:items-center">
          {stateBadge.label ? (
            <StatusBadge
              label={stateBadge.label}
              colorOverride={stateBadge.colorOverride}
            />
          ) : null}
        </div>
      </div>

      <div className="space-y-3">
        <h5 className="text-sm font-semibold text-gray-700">
          Prioritized tasks
        </h5>
        {topTasks.length === 0 ? (
          <p className="text-sm text-gray-600">
            No top tasks available for this entity.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {topTasks.map((task) => (
              <li
                key={task.taskId || `${entity.entityId}-${task.rank}`}
                className="grid gap-3 py-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-800">
                    {task.taskTitle || task.taskName || task.taskId || "Task"}
                  </p>
                  {task.summary && (
                    <p className="text-xs text-gray-500">{task.summary}</p>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-800">Rank:</span>{" "}
                  {task.rank != null ? Number(task.rank).toFixed(3) : "—"}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-800">Due:</span>{" "}
                  {formatDue(task.dueInDays, task.dueDate)}
                </div>
                <div className="flex items-center justify-end">
                  <TaskCtaButton cta={task.cta || task.ctaUrl} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {Array.isArray(entity?.NBAs) && entity.NBAs.length > 0 && (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-800">
          <p className="font-semibold">Next best actions</p>
          <ul className="list-disc pl-5 text-indigo-700">
            {entity.NBAs.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
};

EntityAnalysisCard.propTypes = {
  entity: PropTypes.object.isRequired,
};

const TaskCtaButton = ({ cta }) => {
  if (!cta) {
    return (
      <button
        type="button"
        disabled
        className="cursor-not-allowed rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-400"
      >
        No action
      </button>
    );
  }

  if (typeof cta === "string") {
    return (
      <a
        href={cta}
        className="inline-flex items-center rounded-md bg-sflPrimary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90"
      >
        Open task
      </a>
    );
  }

  const { url, href, label } = cta || {};
  const destination = url || href;

  if (!destination) {
    return (
      <button
        type="button"
        disabled
        className="cursor-not-allowed rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-400"
      >
        No action
      </button>
    );
  }

  return (
    <a
      href={destination}
      className="inline-flex items-center rounded-md bg-sflPrimary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90"
    >
      {label || "Open task"}
    </a>
  );
};

TaskCtaButton.propTypes = {
  cta: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

TaskCtaButton.defaultProps = {
  cta: null,
};

const PaginationControls = ({ page, totalPages, onChange }) => (
  <div className="flex items-center justify-end gap-3">
    <button
      type="button"
      onClick={() => onChange(Math.max(1, page - 1))}
      disabled={page <= 1}
      className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-60"
    >
      Previous
    </button>
    <span className="text-sm text-gray-600">
      Page {page} of {totalPages}
    </span>
    <button
      type="button"
      onClick={() => onChange(Math.min(totalPages, page + 1))}
      disabled={page >= totalPages}
      className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-60"
    >
      Next
    </button>
  </div>
);

PaginationControls.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const STATE_COLOR_MAP = {
  compliant: {
    label: "Compliant",
    colorOverride: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
  notcompliant: {
    label: "Not Compliant",
    colorOverride: "bg-red-100 text-red-700 border-red-200",
  },
  due: {
    label: "Due",
    colorOverride: "bg-amber-100 text-amber-700 border-amber-200",
  },
  notdue: {
    label: "Not Due",
    colorOverride: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
  monitor: {
    label: "Monitor",
    colorOverride: "bg-indigo-100 text-indigo-700 border-indigo-200",
  },
  review: {
    label: "Review",
    colorOverride: "bg-blue-100 text-blue-700 border-blue-200",
  },
};

const resolveStateBadge = (state) => {
  if (!state) {
    return { label: "", colorOverride: null };
  }

  const normalized = String(state).replace(/\s+/g, "").toLowerCase();
  const mapping = STATE_COLOR_MAP[normalized];

  if (mapping) {
    return mapping;
  }

  return {
    label: formatStateLabel(state),
    colorOverride: "bg-slate-100 text-slate-700 border-slate-200",
  };
};

const formatStateLabel = (value) => {
  if (!value) return "";
  const normalized = String(value)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return normalized.replace(/(^|\s)\S/g, (char) => char.toUpperCase());
};

const formatDue = (dueInDays, dueDate) => {
  if (dueInDays == null) {
    if (dueDate) {
      return formatDateDisplay(dueDate);
    }
    return "—";
  }

  const parsed = Number(dueInDays);
  if (Number.isNaN(parsed)) {
    return "—";
  }

  if (parsed === 0) return "Due today";
  if (parsed > 0) {
    return `Due in ${parsed} day${parsed === 1 ? "" : "s"}`;
  }
  const absolute = Math.abs(parsed);
  return `Overdue by ${absolute} day${absolute === 1 ? "" : "s"}`;
};

export default ComplianceAnalysis;
